import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Space, Tag, Tooltip, Typography } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import useLocation from '../hooks/useLocation';
import LocationCascade from '../components/LocationCascade';
import { LocationDTO } from '@/shared/api/model';

const { Title, Text } = Typography;

const LocationManagementView: React.FC = () => {
    const { dark } = useTheme();
    const {
        zones,
        regions,
        districts,
        wards,
        streets,
        selectedZone,
        selectedRegion,
        selectedDistrict,
        selectedWard,
        selectedStreet,
        handleZoneChange,
        isLoading,
        locations
    } = useLocation();
    
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filteredLocations, setFilteredLocations] = useState<LocationDTO[]>([]);
    const [currentParentType, setCurrentParentType] = useState<string>('ZONE');
    const [currentParent, setCurrentParent] = useState<LocationDTO | null>(null);
    const [currentChildrenLocations, setCurrentChildrenLocations] = useState<LocationDTO[]>([]);

    // Update currentParentType based on selections
    useEffect(() => {
        if (selectedStreet) {
            setCurrentParentType('STREET');
            setCurrentParent(selectedStreet);
        } else if (selectedWard) {
            setCurrentParentType('WARD');
            setCurrentParent(selectedWard);
        } else if (selectedDistrict) {
            setCurrentParentType('DISTRICT');
            setCurrentParent(selectedDistrict);
        } else if (selectedRegion) {
            setCurrentParentType('REGION');
            setCurrentParent(selectedRegion);
        } else if (selectedZone) {
            setCurrentParentType('ZONE');
            setCurrentParent(selectedZone);
        } else {
            setCurrentParentType('COUNTRY');
            setCurrentParent(null);
        }
    }, [selectedZone, selectedRegion, selectedDistrict, selectedWard, selectedStreet]);

    // Update current children locations
    useEffect(() => {
        switch (currentParentType) {
            case 'COUNTRY':
                setCurrentChildrenLocations(zones);
                break;
            case 'ZONE':
                setCurrentChildrenLocations(regions);
                break;
            case 'REGION':
                setCurrentChildrenLocations(districts);
                break;
            case 'DISTRICT':
                setCurrentChildrenLocations(wards);
                break;
            case 'WARD':
                setCurrentChildrenLocations(streets);
                break;
            default:
                setCurrentChildrenLocations([]);
        }
    }, [currentParentType, zones, regions, districts, wards, streets]);

    // Handle search filtering
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredLocations(currentChildrenLocations);
        } else {
            const lowercaseSearch = searchTerm.toLowerCase();
            const filtered = currentChildrenLocations.filter(
                location => location.name?.toLowerCase().includes(lowercaseSearch) || false
            );
            setFilteredLocations(filtered);
        }
    }, [searchTerm, currentChildrenLocations]);

    // Table columns configuration
    const columns = [
        {
            title: 'Location Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: LocationDTO) => (
                <Text strong>{text || ''}</Text>
            )
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 120
        },
        {
            title: 'Directory',
            dataIndex: 'directoryId',
            key: 'directoryId',
            width: 150,
            render: (_: any, record: LocationDTO) => (
                record.directoryId || 'N/A'
            )
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive: string) => (
                <Tag color={isActive === 'Y' ? 'green' : 'red'}>
                    {isActive === 'Y' ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_: any, record: LocationDTO) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button 
                            type="text" 
                            icon={<EyeOutlined />} 
                            size="small" 
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button 
                            type="text" 
                            icon={<EditOutlined />} 
                            size="small"
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div className="space-y-4">
            <div className="mb-4">
                <Typography.Title level={2}>Location Management</Typography.Title>
            </div>
            
            <Card className="mb-4">
                <Typography.Title level={5}>Location Navigation</Typography.Title>
                <Text className="mb-4 block">
                    Select location hierarchy to navigate through different levels
                </Text>
                <LocationCascade 
                    showLabels={true}
                    zone={true}
                    region={true}
                    district={true}
                    ward={true}
                    street={true}
                />
            </Card>

            <Card
                title={
                    <div className="flex justify-between items-center">
                        <span>
                            {currentParent ? `${currentParent.name} - Sub Locations` : 'Zones'} 
                            ({filteredLocations.length})
                        </span>
                        <Space>
                            <Input 
                                placeholder="Search locations" 
                                prefix={<SearchOutlined />} 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: 250 }}
                            />
                            <Button 
                                type="primary" 
                                icon={<PlusOutlined />}
                            >
                                Add New Location
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Table
                    columns={columns}
                    dataSource={filteredLocations}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{ 
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50']
                    }}
                />
            </Card>
        </div>
    );
};

export default LocationManagementView;
